import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";

import { enquiryStatusOptions } from "Common/Components/StatusDropdown/statusOptions";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  deleteProductEnquiry,
  deleteServiceEnquiry,
  handleProductEnquiryStatus,
  handleServiceEnquiryStatus,
} from "slices/enquiry/thunk";
import MasterListPage from "./MasterListPage";

import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import { API_ENDPOINT } from "../../helpers/Endpoints/endpoints";
import { ENQ, ENQUIRY_TYPE } from "Constants/StatusConstant";
import FollowupModel from "components/FollowUpModal/FollowUpModel";
import StatusDropdown from "./Components/statusDropdown";
import StatusRemarkModal from "Common/Components/StatusRemarkModal/StatusRemarkModal";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import { showConfirm, showSuccess, showLoading, hideLoading, showError } from "helpers/swalHelper";
import { downloadCalibrationEngineerPdf, downloadRequisitionPdf } from "slices/sales_order/thunk";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DOMPurify from "dompurify";
import Tooltip from "@mui/material/Tooltip";
import Chip from "components/Chips/Chip";
import FollowUpPopUp from "components/FollowUpModal/FollowUpPopUp";
import StatusChip from "./Components/StatusChip";
import { app_modules } from "helpers/apps_modules_list";
import { usePermission } from "hooks/usePermission";

interface EnquiryPageProps {
  type: "product" | "service";
}

const EnquiryLogPage: React.FC<EnquiryPageProps> = ({ type }) => {
  document.title = type === "product" ? "Sales Enquiry" : "Service Enquiry";
  const navigate = useNavigate();
  const [reloadFlag, setReloadFlag] = useState(false);
  // Status Change Handle
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [remark, setRemark] = useState("");

  // Follow Up Modal  states
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [selectedFollowUpItem, setSelectedFollowUpItem] = useState<any>(null);

  const dispatch = useDispatch<any>();
  const activeType = type; // product by default
  const addButtonLabel =
    activeType === "product"
      ? "Create Sales Enquiry"
      : "Create Service Enquiry";

  const addRoute =
    activeType === "product"
      ? `/enquiry/add-product-enquiry`
      : `/enquiry/add-services-enquiry`;

  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.enquiry?.app_name,
    activeType === "product"
      ? app_modules?.enquiry?.modules?.product
      : app_modules?.enquiry?.modules?.service
  );

  const handleView = (enquiryId: string) => {
    navigate(`/enquiry/${type}/enquiry_log/view/${enquiryId}`);
  };

  const handleEdit = (enquiryId: string) => {
    navigate(`/enquiry/${type}/edit/${enquiryId}`);
  };

  const handleDelete = async (enquiryId: string) => {
    const result = await showConfirm(
      "This enquiry will be permanently deleted. Do you want to continue?",
      "Delete Enquiry",
      "Yes, delete",
      "Cancel"
    );

    if (!result.isConfirmed) return;
    try {
      if (activeType === "product") {
        await dispatch(deleteProductEnquiry(enquiryId)).unwrap();
      } else {
        await dispatch(deleteServiceEnquiry(enquiryId)).unwrap();
      }

      toast.success("Enquiry deleted successfully");
      setReloadFlag((prev) => !prev); // ✅ trigger re-fetch
    } catch (error: any) {
      toast.error(error || "Failed to delete enquiry");
    }
  };

  const handleDownloadCalibrationEngineerPdf = async (
    salesOrderServiceId: string,
    salesOrderNumber: string,
  ) => {
    try {
      showLoading("Downloading PDF...");
      const res = await dispatch(
        downloadCalibrationEngineerPdf({
          salesOrderServiceId,
          salesOrderNumber,
        }),
      ).unwrap();

      hideLoading();
      if (res) {
        showSuccess("GRN Tech Form PDF downloaded successfully");
      }
    } catch (err: any) {
      hideLoading();
      showError(
        err?.message ||
        err?.error ||
        "Failed to download GRN Tech Form PDF",
      );
    }
  };

  const handleDownloadRequisitionPdf = async (
    salesOrderServiceId: string,
    salesOrderNumber: string,
  ) => {
    try {
      showLoading("Downloading PDF...");
      const res = await dispatch(
        downloadRequisitionPdf({
          salesOrderServiceId,
          salesOrderNumber,
        }),
      ).unwrap();

      hideLoading();
      if (res) {
        showSuccess("GRN Admin Form PDF downloaded successfully");
      }
    } catch (err: any) {
      hideLoading();
      showError(
        err?.message || err?.error || "Failed to download GRN Admin Form PDF",
      );
    }
  };

  const openStatusModal = (itemId: string, status: string) => {
    setSelectedItemId(itemId);
    setSelectedStatus(status);
    setRemark("");
    setStatusModalOpen(true);
  };

  const handleStatusSubmit = async () => {
    if (!selectedItemId) return;

    await handleStatusChange(
      selectedItemId,
      selectedStatus,
      remark // 👈 pass remark
    );

    setStatusModalOpen(false);
  };

  const handleStatusChange = async (
    enquiryId: string,
    newStatus: string,
    remark: string
  ) => {
    try {
      if (activeType === "product") {
        await dispatch(
          handleProductEnquiryStatus({
            id: enquiryId,
            enquiry_stage: "enquiry_log",
            status: newStatus,
            remark,
          })
        ).unwrap();
      } else {
        await dispatch(
          handleServiceEnquiryStatus({
            id: enquiryId,
            enquiry_stage: "enquiry_log",
            status: newStatus,
            remark,
          })
        ).unwrap();
      }

      // toggle flag to trigger useEffect
      setReloadFlag((prev) => !prev);
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const getColumns = () => {
    const commonColumns = [
      // {
      //   header: "Sr. No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      // },
      {
        header: "Enquiry No.",
        accessorKey: "enquiry_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "enquiry_number",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
      },
      ...(activeType === "service"
        ? [
          {
            header: "Service Number",
            accessorKey: "service_number",
            enableColumnFilter: true,
            enableSorting: true,
            filterType: "dropdown" as const,
            filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
              "service_number",
              activeType,
              ENQ.ENQUIRY_LOG
            )}`,
            cell: ({ row }: { row: any }) =>
              row.original?.service_number || "-",
          },
          {
            header: "Organisation",
            accessorKey: "bill_from",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) =>
              row.original?.bill_from?.organization_name || "-",
          },
        ]
        : []),
      {
        header: "Enquiry Date",
        accessorKey: "enquiry_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.enquiry_date} />
        ),
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "customer_name",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
        cell: ({ row }: { row: any }) => row.original?.customer_name || "-",
      },
      {
        header: "Source of Enquiry",
        accessorKey: "source_of_enquiry",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "source_of_enquiry",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
        cell: ({ row }: { row: any }) => row.original?.source_of_enquiry || "-",
      },
      {
        header: "Agent",
        accessorKey: "agent_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "agent_name",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
        cell: ({ row }: { row: any }) => row.original?.agent_name || "-",
      },
      {
        header: "Reference",
        accessorKey: "reference_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "reference_number",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
        cell: ({ row }: { row: any }) => row.original?.reference_number || "-",
      },
      {
        header: "Status",
        accessorKey: "enquiry_status",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "enquiry_status",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
        cell: ({ row }: { row: any }) => {
          return (
            <StatusDropdown
              currentStatus={row.original.enquiry_status}
              itemId={
                activeType == "product"
                  ? row.original.enquiry_product_id
                  : row.original.enquiry_service_id
              }
              openStatusModal={openStatusModal}
              statusOptions={enquiryStatusOptions}
              disabled={false}
            />
          );
        },
      },
      {
        header: "Assigned To",
        accessorKey: "assigned_members",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "assigned_members",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,

        cell: ({ row }: { row: any }) => {
          const members = row.original?.assigned_members || [];

          if (!members.length) return "-";

          const names = members.map(
            (m: any) =>
              `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.username
          );

          const visible = names.slice(0, 4);
          const remaining = names.length - visible.length;

          return (
            <Tooltip
              arrow
              placement="top"
              title={names.join("\n")}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 250,
                    fontSize: "13px",
                  },
                },
              }}
            >
              <div style={{ cursor: "pointer", lineHeight: 1.4 }}>
                {visible.map((name: string, index: number) => (
                  <div key={index}>{name}</div>
                ))}

                {remaining > 0 && (
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    +{remaining} more
                  </div>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Quotation Date",
        accessorKey: "quotation_date",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.quotation_date} />
        ),
      },
      {
        header: "Order Status",
        accessorKey: "order_status",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "order_status",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,
        cell: ({ row }: { row: any }) => (
          <StatusChip status={row.original?.order_status} />
        ),
      },
      {
        header: "Last Follow-up Date",
        accessorKey: "follow_up_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          return (
            <FormattedDate
              date={row.original?.enquiry_follow_ups?.[0]?.follow_up_date}
            />
          );
        },
      },
      {
        header: "Follow-up Remark",
        accessorKey: "follow_up_remarks",
        enableColumnFilter: false,
        enableSorting: false,

        size: 300, // 👈 fixed width
        minSize: 300,
        maxSize: 300,

        cell: ({ row }: { row: any }) => {
          const rawHtml =
            row.original?.enquiry_follow_ups?.[0]?.follow_up_remarks || "";

          if (!rawHtml) return <span>-</span>;

          const safeHtml = DOMPurify.sanitize(rawHtml);

          return (
            <Tooltip
              arrow
              placement="top"
              title={
                <div
                  style={{
                    maxWidth: "450px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
              }
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 500,
                    p: 1.5,
                  },
                },
              }}
            >
              <div
                style={{
                  width: "200px", // 👈 enforce inside cell
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  cursor: "pointer",
                }}
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            </Tooltip>
          );
        },
      },

      {
        header: "Next Follow-up Date",
        accessorKey: "next_follow_up_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        // filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN('company_name', activeType)}`,
        cell: ({ row }: { row: any }) => (
          <FormattedDate
            date={row.original?.enquiry_follow_ups?.[0]?.next_follow_up_date}
          />
        ),
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user?.username || "-"}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.updated_by_user.username}
            date={row.original.updated_at}
          />
        ),
      },
    ];

    const actionColumn = {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const enquiryId =
          activeType == "product"
            ? row.original.enquiry_product_id
            : row.original.enquiry_service_id;

        const data = row.original;

        const menuItems = canView || canUpdate ? [
          {
            icon: <VisibilityIcon fontSize="small" color="info" />,
            permission: canView,
            label: "View",
            onClick: () => handleView(enquiryId),
          },

          ...(row?.original?.enquiry_status?.toLowerCase() === "in progress"
            ? [
              {
                icon: <EditIcon fontSize="small" color="primary" />,
                label: "Edit",
                permission: canUpdate,
                onClick: () => handleEdit(enquiryId),
              },
            ]
            : []),

          {
            icon: <PersonAddIcon fontSize="small" color="success" />,
            label: "Follow Up",
            permission: canUpdate,
            // onClick: () => handleFollowUp(enquiryId),
            onClick: (item: any) => {
              setSelectedFollowUpItem(data);
              setShowFollowUpModal(true);
            },
          },

          ...(row?.original?.enquiry_status?.toLowerCase() !== "converted"
            ? [
              {
                label: "Delete",
                icon: <DeleteOutlineIcon fontSize="small" color="error" />,
                permission: canUpdate,
                onClick: () => handleDelete(enquiryId),
                className: "text-danger",
              },
            ]
            : []),

          ...(activeType === "service" && canView
            ? [
              {
                label: "GRN Admin Form PDF",
                permission: canView,
                icon: <FileDownloadOutlinedIcon fontSize="small" color="primary" />,
                onClick: async () => {
                  handleDownloadRequisitionPdf(
                    data.sales_order_service_id || enquiryId,
                    data.sales_order_number || data.enquiry_number || "",
                  );
                },
              },
              {
                label: "GRN Tech Form PDF",
                permission: canView,
                icon: <FileDownloadOutlinedIcon fontSize="small" color="primary" />,
                onClick: async () => {
                  handleDownloadCalibrationEngineerPdf(
                    data.sales_order_service_id || enquiryId,
                    data.sales_order_number || data.enquiry_number || "",
                  );
                },
              },
            ]
            : []),
          // {
          //   icon: <DescriptionIcon fontSize="small" color="warning" />,
          //   label: "Create Quotation",
          //   onClick: () => handleNavigateToQuotation(enquiryId),
          // },
          // {
          //   icon: <DeleteOutlineIcon fontSize="small" color="error" />,
          //   label: "Junk",
          //   onClick: () => handleConvertJunk(enquiry_product_id),
          // },
        ] : [];

        return <DynamicMenuItem row={row.original} menuItems={menuItems} />;
      },
    };

    return [...commonColumns, actionColumn];
  };

  useEffect(() => {
    console.log("selectedFollowUpItem", selectedFollowUpItem);
  }, [selectedFollowUpItem]);

  useEffect(() => {
    setReloadFlag((prev) => !prev);
  }, [showFollowUpModal]);

  return (
    <>
      <MasterListPage
        activeTab="enquiry_log"
        title={`${activeType === "product" ? "Sales" : "Service"} Enquiry`}
        addButtonLabel={addButtonLabel}
        addRoute={addRoute}
        columns={getColumns()}
        statusToggleUrl={`/enquiry/${activeType}`}
        activeType={activeType}
        reloadFlag={reloadFlag}
      />

      <FollowUpPopUp
        open={showFollowUpModal}
        onClose={() => setShowFollowUpModal(false)}
        title="Follow-up and Activities"
      >
        {selectedFollowUpItem && (
          <FollowupModel
            data={selectedFollowUpItem}
            activeType={
              activeType === "product"
                ? ENQUIRY_TYPE?.PRODUCT
                : ENQUIRY_TYPE?.SERVICE
            }
            onClose={() => setShowFollowUpModal(false)}
          />
        )}
      </FollowUpPopUp>


      {/* Remark Pop Up */}
      <StatusRemarkModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onSubmit={handleStatusSubmit}
        remark={remark}
        onRemarkChange={setRemark}
      />
    </>
  );
};

export default EnquiryLogPage;
